/**
 * Sentry Error Tracking Configuration
 *
 * Captures and reports errors with rich context for debugging.
 * Filters sensitive blockchain data (addresses, private keys, amounts).
 */

import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry error tracking
 * Call this before rendering the React app
 */
export function initSentry() {
  // Only initialize in production or if explicitly enabled
  const isProduction = process.env.NODE_ENV === 'production';
  const sentryEnabled = process.env.REACT_APP_SENTRY_ENABLED === 'true';

  if (!isProduction && !sentryEnabled) {
    return;
  }

  const sentryDsn = process.env.REACT_APP_SENTRY_DSN;

  if (!sentryDsn) {
    console.warn('Sentry DSN not configured. Set REACT_APP_SENTRY_DSN in .env file');
    return;
  }

  try {
    Sentry.init({
    dsn: sentryDsn,

    // Environment (production, staging, development)
    environment: process.env.REACT_APP_ENV || process.env.NODE_ENV || 'production',

    // Release version for tracking fixes
    release: process.env.REACT_APP_VERSION || 'unknown',

    // Performance Monitoring (10% of transactions)
    integrations: [
      Sentry.browserTracingIntegration({
        // Track navigation between pages
        tracePropagationTargets: [
          "localhost",
          /^https:\/\/coincommons\.io/,
          /^https:\/\/.*\.coincommons\.io/
        ],
      }),
      Sentry.replayIntegration({
        // Mask all text and images for privacy
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],

    // Performance monitoring sample rate (0.0 to 1.0)
    tracesSampleRate: 0.1, // 10% of transactions

    // Session Replay sample rates
    replaysSessionSampleRate: 0.1, // 10% of normal sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Filter and sanitize data before sending
    beforeSend(event, hint) {
      // Remove sensitive blockchain data
      event = filterSensitiveData(event);

      return event;
    },

    // Filter breadcrumbs
    beforeBreadcrumb(breadcrumb) {
      // Remove sensitive data from breadcrumbs
      if (breadcrumb.data) {
        breadcrumb.data = filterSensitiveObject(breadcrumb.data);
      }
      return breadcrumb;
    },

    // Ignore certain errors
    ignoreErrors: [
      // Browser extensions
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',

      // Network errors (expected in crypto apps)
      'NetworkError',
      'Failed to fetch',
      'Network request failed',

      // User cancelled actions (expected)
      'User rejected',
      'User denied',
      'User cancelled',
      'ACTION_REJECTED',

      // Metamask specific (user actions)
      'MetaMask Tx Signature: User denied transaction signature',
      'User rejected the request',

      // WalletConnect (expected user cancellations and session errors)
      'User rejected',
      'User closed modal',
      'session topic doesn\'t exist',
      'No matching key',
      /session topic doesn't exist/,
      /No matching key.*session topic/,
    ],

    // Don't report errors from these URLs (browser extensions, etc)
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
    ],
    });

    // Expose Sentry globally for debugging in console
    if (typeof window !== 'undefined') {
      window.Sentry = Sentry;
    }
  } catch (error) {
    console.error('❌ Failed to initialize Sentry:', error);
  }
}

/**
 * Filter sensitive blockchain data from error context
 */
function filterSensitiveData(event) {
  // Filter user data
  if (event.user) {
    delete event.user.ip_address; // Don't track IP
    if (event.user.id && event.user.id.startsWith('0x')) {
      // Truncate wallet address for privacy
      event.user.id = truncateAddress(event.user.id);
    }
  }

  // Filter extra context
  if (event.extra) {
    event.extra = filterSensitiveObject(event.extra);
  }

  // Filter request data
  if (event.request?.data) {
    event.request.data = filterSensitiveObject(event.request.data);
  }

  // Filter tags
  if (event.tags) {
    event.tags = filterSensitiveObject(event.tags);
  }

  return event;
}

/**
 * Recursively filter sensitive fields from objects
 */
function filterSensitiveObject(obj) {
  if (!obj || typeof obj !== 'object') return obj;

  const filtered = { ...obj };
  const sensitiveKeys = [
    'privateKey',
    'private_key',
    'mnemonic',
    'seed',
    'password',
    'secret',
    'token',
    'apiKey',
    'api_key',
    'walletAddress',
    'wallet_address',
    'address',
    'txHash',
    'tx_hash',
    'transactionHash',
    'amount',
    'balance',
    'email',
  ];

  for (const key in filtered) {
    const lowerKey = key.toLowerCase();

    // Remove sensitive keys
    if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive.toLowerCase()))) {
      if (lowerKey.includes('address') || lowerKey.includes('hash')) {
        // Truncate addresses and hashes instead of removing
        filtered[key] = truncateAddress(String(filtered[key]));
      } else {
        filtered[key] = '[REDACTED]';
      }
    }

    // Recursively filter nested objects
    if (typeof filtered[key] === 'object' && filtered[key] !== null) {
      filtered[key] = filterSensitiveObject(filtered[key]);
    }
  }

  return filtered;
}

/**
 * Truncate blockchain address for privacy
 */
function truncateAddress(address) {
  if (!address || typeof address !== 'string') return address;
  if (address.length < 10) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

/**
 * Set user context for error tracking
 * Call when user connects wallet or logs in
 */
export function setSentryUser(userId, metadata = {}) {
  Sentry.setUser({
    id: truncateAddress(userId), // Truncated for privacy
    ...filterSensitiveObject(metadata),
  });
}

/**
 * Clear user context
 * Call when user disconnects wallet or logs out
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Add context tag for filtering errors
 */
export function setSentryTag(key, value) {
  Sentry.setTag(key, value);
}

/**
 * Add breadcrumb for debugging user flow
 */
export function addSentryBreadcrumb(message, category, data = {}) {
  Sentry.addBreadcrumb({
    message,
    category,
    data: filterSensitiveObject(data),
    level: 'info',
  });
}

/**
 * Manually capture an exception
 */
export function captureSentryException(error, context = {}) {
  Sentry.captureException(error, {
    extra: filterSensitiveObject(context),
  });
}

/**
 * Manually capture a message
 */
export function captureSentryMessage(message, level = 'info', context = {}) {
  Sentry.captureMessage(message, {
    level,
    extra: filterSensitiveObject(context),
  });
}

// Export Sentry for advanced usage
export { Sentry };
